import { CSS_HOJA, type DatosDeHoja, HojaISO } from "@gestiondoc/plantilla";
import { ANCHO_HOJA_HORIZONTAL_PX, ANCHO_HOJA_PX, orientacionDe } from "@gestiondoc/shared";
import { type ReactNode, useEffect, useRef, useState } from "react";

/**
 * La vista previa: la misma `HojaISO` que imprime el worker, escalada para caber en la columna.
 * La hoja se dibuja a tamaño real y se reduce con `transform`, así los saltos de línea y las
 * proporciones son los del papel. El CSS de la hoja se inyecta una sola vez por página.
 *
 * `encima` va sobre la hoja, en el mismo sistema de coordenadas (píxeles de la hoja a tamaño
 * real): es la capa de tinta de las hojas llenas.
 */
export function VistaHoja({ datos, encima }: { datos: DatosDeHoja; encima?: ReactNode }) {
  const marco = useRef<HTMLDivElement>(null);
  const hoja = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);
  const [alto, setAlto] = useState(0);
  const ancho =
    orientacionDe(datos.contenido) === "horizontal" ? ANCHO_HOJA_HORIZONTAL_PX : ANCHO_HOJA_PX;

  useEffect(() => {
    const m = marco.current;
    const h = hoja.current;
    if (!m || !h) return;
    const medir = () => {
      const e = Math.min(1, m.clientWidth / ancho);
      setEscala(e);
      setAlto(h.offsetHeight * e);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(m);
    ro.observe(h);
    return () => ro.disconnect();
  }, [ancho]);

  return (
    <div ref={marco} className="vista-hoja" style={{ height: alto || undefined }}>
      <style>{CSS_HOJA}</style>
      <div
        ref={hoja}
        className="vista-hoja-papel"
        style={{ width: ancho, transform: `scale(${escala})`, transformOrigin: "top left" }}
      >
        <HojaISO datos={datos} modo="pantalla" />
        {encima}
      </div>
    </div>
  );
}
